import {getTestProps} from '@benchling/js-test-utils';
import {Chip, Input, Stack, Text, TextInput} from '@mantine/core';
import React, {useMemo} from 'react';

import SelectOrCreateDatasetInput from 'data-frames/components/SelectOrCreateDatasetInput';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import StepConfigEditorPane from 'pipelines/generalized-step-config-editor/StepConfigEditorPane';
import SelectableCard from 'ui-library/inputs/SelectableCard';
import pick from 'util/pick';
import {MakeFieldsRequired} from 'util/Types';

import RightSidebarSkeleton, {RightSidebarWidth} from '../RightSidebarSkeleton';

export enum TemplateInputType {
  VARIABLE = 'VARIABLE',
  STATIC = 'STATIC',
}

export interface ConfigureTemplateInputState {
  templateInputType: TemplateInputType;
  datasetId: string | null;
  name: string;
  description: string;
  requireColumnMapping: boolean;
}

function isConfigureTemplateInputStateComplete(
  state: ConfigureTemplateInputState
): state is MakeFieldsRequired<ConfigureTemplateInputState, 'datasetId' | 'name'> {
  return state.datasetId != null && state.name != null && state.name.trim().length > 0;
}

export const DEFAULT_CONFIGURE_TEMPLATE_INPUT_STATE: ConfigureTemplateInputState = {
  templateInputType: TemplateInputType.VARIABLE,
  datasetId: null,
  name: '',
  description: '',
  requireColumnMapping: false,
};

/**
 * Returns true if the configured state requires a second phase to configure the `APPLY_COLUMN_MAPPING` step.
 */
export function shouldAddApplyColumnMappingStep(state: ConfigureTemplateInputState): boolean {
  return state.templateInputType === TemplateInputType.VARIABLE && !state.requireColumnMapping;
}

interface ConfigureTemplateInputPhaseProps {
  state: ConfigureTemplateInputState;
  setState: (state: ConfigureTemplateInputState) => void;
  onCancel: () => void;
  onNext: () => void;
  nextButtonLabel?: string;
}

/**
 * The first phase of the `AnalysisRightSidebarItemType.ADD_TEMPLATE_INPUT_SOURCE` sidebar, in which the user
 * configures a variable (`PipelineStepType.VARIABLE_ANALYSIS_TEMPLATE_TABLE`) or static
 * (`PipelineStepType.STATIC_TEMPLATE_INPUT_TABLE`) input table.
 *
 * If the user creates a variable input table (`templateInputType: TemplateInputType.VARIABLE`) which does not
 * require the end user to manually map columns (`requireColumnMapping: false`), the sidebar will progress to
 * `ConfigureColumnMappingPhase`, to configure a `PipelineStepType.APPLY_COLUMN_MAPPING` step within the
 * template.
 *
 * @package
 */
export default function ConfigureTemplateInputPhase({
  state,
  setState,
  onCancel,
  onNext,
  nextButtonLabel = 'Next',
}: ConfigureTemplateInputPhaseProps): JSX.Element {
  const isComplete = isConfigureTemplateInputStateComplete(state);
  const isVariable = state.templateInputType === TemplateInputType.VARIABLE;

  const variableTemplateInputTableConfig = useMemo(
    () => pick(state, 'datasetId', 'requireColumnMapping', 'name', 'description'),
    [state]
  );

  return (
    <RightSidebarSkeleton
      title="Create table from dataset"
      confirmationButtons={[
        {
          text: 'Cancel',
          onClick: onCancel,
          dataTestAttrsElement: 'cancel',
        },
        {
          text: nextButtonLabel,
          onClick: onNext,
          dataTestAttrsElement: 'next',
          disabled: !isComplete,
          isPrimary: true,
        },
      ]}
      width={RightSidebarWidth.WIDE}
      dataTestAttrs={{
        component: 'ConfigureTemplateInputPhase',
        element: 'root',
      }}
    >
      <Stack spacing="sm">
        <Text size="md" weight={500} mb="xs">
          Create table with:
        </Text>
        <Chip.Group
          multiple={false}
          value={state.templateInputType}
          onChange={(templateInputType: TemplateInputType) => {
            if (templateInputType) {
              setState({...state, templateInputType});
            }
          }}
        >
          <Stack spacing="sm">
            <SelectableCard
              value={TemplateInputType.VARIABLE}
              title="Variable data"
              description="Dataset provided when the template is applied"
              dataTestAttrs={{
                component: 'AddTemplateInputSourceRightSidebar',
                element: 'variableOption',
              }}
            />
            <SelectableCard
              value={TemplateInputType.STATIC}
              title="Static data"
              description="Same dataset each time the template is applied"
              dataTestAttrs={{
                component: 'AddTemplateInputSourceRightSidebar',
                element: 'staticOption',
              }}
            />
          </Stack>
        </Chip.Group>
      </Stack>
      {isVariable ? (
        <StepConfigEditorPane
          stepType={PipelineStepType.VARIABLE_TEMPLATE_INPUT_TABLE}
          stepVersion={0}
          config={variableTemplateInputTableConfig}
          setConfig={(config) => {
            setState({...state, ...config});
          }}
          dataFramesInfos={{}}
          clientStateId="add-column-mapping"
        />
      ) : (
        <StaticTemplateInputConfig state={state} setState={setState} />
      )}
    </RightSidebarSkeleton>
  );
}

/**
 * Configuration to add a static `TEMPLATE_INPUT` step.
 *
 * We do not use the generic `<StepConfigEditorPane>` for static input tables because `TEMPLATE_INPUT` steps
 * are not editable.
 */
function StaticTemplateInputConfig({
  state,
  setState,
}: Pick<ConfigureTemplateInputPhaseProps, 'state' | 'setState'>): JSX.Element {
  return (
    <Stack spacing="md">
      <Input.Wrapper
        id={state.datasetId ?? undefined}
        label="Dataset"
        withAsterisk={true}
        inputWrapperOrder={['label', 'description', 'input']}
      >
        <SelectOrCreateDatasetInput
          id={state.datasetId ?? undefined}
          isMulti={false}
          onRemove={() => {
            setState({...state, datasetId: null});
          }}
          onSelect={(id) => {
            setState({...state, datasetId: id});
          }}
          selected={state.datasetId == null ? [] : [state.datasetId]}
        />
      </Input.Wrapper>
      <TextInput
        label="Table name"
        withAsterisk={true}
        value={state.name}
        onChange={(e) => {
          setState({...state, name: e.target.value});
        }}
        {...getTestProps({
          component: 'AddTemplateInputSourceRightSidebar',
          element: 'nameInput',
        })}
      />
    </Stack>
  );
}
